import { Component, computed, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { TooltipModule } from 'primeng/tooltip';
import { TranslocoModule } from '@jsverse/transloco';
import { FormsModule } from '@angular/forms';

import { OnboardingService } from '../../services/onboarding.service';
import { JourneyService } from '../../services/journey.service';
import { GreentripperPinIconComponent } from '../greentripper-pin-icon/greentripper-pin-icon.component';
import { JourneyHeaderComponent } from '../journey-header/journey-header.component';
import { JourneyNextStepComponent } from '../journey-next-step/journey-next-step.component';

/**
 * The onboarding popup: the journey recap and the next action, to bring the user back to a
 * path they have not finished (see OnboardingService.promptIfNeeded).
 *
 * It used to open on the intro video for first-time users. The video now lives only on the
 * Getting Started page, as the `watch_intro_video` step of the journey — so a newcomer gets
 * the same recap as everybody else, worded as a welcome rather than a reminder.
 */
@Component({
  selector: 'app-onboarding-popup',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    CheckboxModule,
    TooltipModule,
    TranslocoModule,
    FormsModule,
    GreentripperPinIconComponent,
    JourneyHeaderComponent,
    JourneyNextStepComponent,
  ],
  templateUrl: './onboarding-popup.component.html',
})
export class OnboardingPopupComponent {
  private onboardingService = inject(OnboardingService);
  private journeyService = inject(JourneyService);
  private router = inject(Router);

  visible = this.onboardingService.visible;

  dontShowAgain = false;
  isLoading = false;

  /**
   * Nothing ticked yet: the popup greets rather than reminds. Same layout either way — only
   * the header and the call to action change.
   */
  private isStarting = computed(
    () => this.journeyService.completedCount() === 0,
  );

  private textKey = computed(() =>
    this.isStarting()
      ? 'help.onboarding.journey.popup.start'
      : 'help.onboarding.journey.popup',
  );

  titleKey = computed(() => `${this.textKey()}.title`);
  subtitleKey = computed(() => `${this.textKey()}.subtitle`);
  ctaKey = computed(() => `${this.textKey()}.cta`);

  /** Summary CTA: closes the popup, then opens the journey page. */
  onResumeJourney(): void {
    this.dismiss();
    this.router.navigate(['/home/help/onboarding']);
  }

  /**
   * Gère la fermeture via X ou Escape
   */
  onHide(): void {
    if (!this.isLoading) {
      this.dismiss();
    }
  }

  /**
   * The only thing the popup has to persist is the "ne plus montrer" opt-out, so closing it
   * usually costs no call at all.
   */
  private dismiss(): void {
    if (!this.dontShowAgain) {
      this.afterDismiss();
      return;
    }

    this.isLoading = true;

    this.journeyService.dismissPopup().subscribe({
      next: () => this.afterDismiss(),
      error: (error) => {
        console.error("Erreur lors de la sauvegarde de l'onboarding:", error);
        this.afterDismiss();
      },
    });
  }

  private afterDismiss(): void {
    this.isLoading = false;
    this.onboardingService.close();
    this.dontShowAgain = false;
  }
}
